import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, BookOpen, BarChart3, Phone, Wrench, DollarSign, Pill, UtensilsCrossed, Dumbbell } from 'lucide-react';

// 5 main nav items with Blog always visible
const navItems = [
  { id: 'home', label: 'Home', icon: Home, path: '/' },
  { id: 'blog', label: 'Blog', icon: BookOpen, path: '/blog' },
  { id: 'tools', label: 'Tools', icon: Wrench, path: '/tools' },
  { id: 'forecast', label: 'Forecast', icon: BarChart3, path: '/calculator' },
  { id: 'call', label: 'Call', icon: Phone, path: 'tel:8016518363' }
];

const toolItems = [
  { id: 'cost', label: 'Cost Calculator', icon: DollarSign, path: '/cost-calculator' },
  { id: 'match', label: 'Medication Match', icon: Pill, path: '/medication-match' },
  { id: 'meal', label: 'Meal Planner', icon: UtensilsCrossed, path: '/meal-plan' },
  { id: 'strength', label: 'Strength Plan', icon: Dumbbell, path: '/strength-plan' }
];

export const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showTools, setShowTools] = useState(false);

  const handleClick = (item) => {
    if (item.path === '/tools') {
      setShowTools(!showTools);
    } else if (item.path.startsWith('tel:')) {
      window.location.href = item.path;
    } else {
      setShowTools(false);
      navigate(item.path);
    }
  };

  const isActive = (item) => {
    if (item.path === '/') {
      return location.pathname === '/';
    }
    if (item.path === '/blog') {
      return location.pathname.startsWith('/blog');
    }
    if (item.path === '/tools') {
      return ['/cost-calculator', '/medication-match', '/meal-plan', '/strength-plan'].includes(location.pathname);
    }
    if (item.path.startsWith('tel:')) {
      return false;
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <>
      {/* Tools Dropdown */}
      {showTools && (
        <div 
          className="fixed bottom-28 left-0 right-0 z-[9998] bg-[#1E293B] border-t border-[#334155] md:hidden"
          data-testid="tools-dropdown"
        >
          <div className="max-w-md mx-auto px-4 py-3 space-y-2">
            {toolItems.map((tool) => {
              const Icon = tool.icon;
              const isToolActive = location.pathname === tool.path;
              return (
                <button
                  key={tool.id}
                  onClick={() => { navigate(tool.path); setShowTools(false); }}
                  className={`flex items-center gap-3 w-full p-3 rounded-lg transition-colors ${
                    isToolActive 
                      ? 'bg-[#3B82F6] text-white' 
                      : 'bg-[#0B1020] hover:bg-[#334155]'
                  }`}
                  data-testid={`tool-${tool.id}`}
                >
                  <Icon size={20} className={isToolActive ? 'text-white' : 'text-[#3B82F6]'} />
                  <span className={`font-medium ${isToolActive ? 'text-white' : 'text-[#F8FAFC]'}`}>{tool.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <nav 
        className="fixed bottom-12 left-0 right-0 z-[9999] bg-[#0B1020] border-t border-b border-[#1E293B] md:hidden"
        data-testid="bottom-nav"
      >
        <div className="flex justify-around items-center h-16 px-1 max-w-md mx-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            
            return (
              <button
                key={item.id}
                onClick={() => handleClick(item)}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  active 
                    ? 'text-[#3B82F6]' 
                    : 'text-[#94A3B8] active:text-[#F8FAFC]'
                }`}
                data-testid={`bottom-nav-${item.id}`}
              >
                <Icon 
                  size={22} 
                  strokeWidth={active ? 2.5 : 2}
                  className={active ? 'text-[#3B82F6]' : ''} 
                />
                <span className={`text-[10px] mt-1 font-medium ${active ? 'text-[#3B82F6]' : ''}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
        
        {/* Safe area for iPhone home indicator */}
        <div className="h-[env(safe-area-inset-bottom)] bg-[#0B1020]" />
      </nav>
    </>
  );
};

export default BottomNav;
